import { useState, useEffect, useRef } from "react";
import { useStateValue } from "../StateProvider";
import { spotifyRateLimiter } from "../utils/SpotifyRateLimiter";
import "./SpotifyPlayer.css";
import IOSSpotifySetup from "./IOSSpotifySetup";
import IOSErrorModal from "./IOSErrorModal";

export default function SpotifyPlayer() {
  const [position, setPosition] = useState(0); // in seconds
  const [duration, setDuration] = useState(0); // in seconds
  const [track, setTrack] = useState({});
  const [message, setMessage] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [playTimer, setPlayTimer] = useState(0); // in seconds
  const [showIOSSetup, setShowIOSSetup] = useState(false);
  const [isSpotifyAppOpen, setIsSpotifyAppOpen] = useState(false);
  const [showIOSErrorModal, setShowIOSErrorModal] = useState(null); // 'pause' | 'wrongSong' | null

  const timerInterval = useRef(null);
  const trackCheckInterval = useRef(null);
  const progressInterval = useRef(null);
  const playStartedAt = useRef(0);

  // Web Playback SDK (desktop only): in-browser Spotify device
  const sdkPlayerRef = useRef(null);
  const sdkDeviceIdRef = useRef(null);
  const sdkFailedRef = useRef(false);
  const tokenRef = useRef(null);



  const [{ token, songToPlay, hideSpotifyPlayerUI, triggerPlayFromClick, subscriptionConfig}, dispatch] = useStateValue();

  // Calculate actual listening time requirement based on song duration
  // For songs shorter than the tier requirement, use song duration - 1 second
  const calculateListeningTimeRequired = () => {
    const tierRequirement = subscriptionConfig?.listeningTimeRequired || 55; // default to free tier

    // If we don't have duration yet, use tier requirement
    if (!duration || duration === 0) {
      return tierRequirement;
    }

    // If song is shorter than tier requirement, use song duration - 1 second (minimum 3 seconds)
    if (duration < tierRequirement) {
      return Math.max(3, duration - 1);
    }

    // Otherwise use tier requirement
    return tierRequirement;
  };

  const listeningTimeRequired = calculateListeningTimeRequired();

  // Desktop plays in the browser via the Web Playback SDK, mobile via the Spotify app
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isSdkPlayback = () => !isMobile && !sdkFailedRef.current && !!sdkDeviceIdRef.current;

  // Keep latest token available for the SDK's getOAuthToken callback
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Initialize Web Playback SDK on desktop
  useEffect(() => {
    if (isMobile || !token || sdkPlayerRef.current) return;

    const initPlayer = () => {
      if (sdkPlayerRef.current) return;

      const player = new window.Spotify.Player({
        name: "Song Carousel Web Player",
        getOAuthToken: (cb) => cb(tokenRef.current),
        volume: 0.8,
      });

      player.addListener("ready", ({ device_id }) => {
        console.log("✅ Web Playback SDK ready with device ID:", device_id);
        sdkDeviceIdRef.current = device_id;
      });

      player.addListener("not_ready", ({ device_id }) => {
        console.warn("Web Playback SDK device went offline:", device_id);
        sdkDeviceIdRef.current = null;
      });

      // On any SDK error fall back to Spotify app playback
      // (account_error = no Spotify Premium, authentication_error = token lacks "streaming" scope)
      ["initialization_error", "authentication_error", "account_error"].forEach((event) => {
        player.addListener(event, ({ message }) => {
          console.warn(`Web Playback SDK ${event} - falling back to Spotify app:`, message);
          sdkFailedRef.current = true;
          sdkDeviceIdRef.current = null;
        });
      });

      player.addListener("playback_error", ({ message }) => {
        console.error("Web Playback SDK playback error:", message);
      });

      player.connect();
      sdkPlayerRef.current = player;
    };

    if (window.Spotify?.Player) {
      initPlayer();
    } else {
      window.onSpotifyWebPlaybackSDKReady = initPlayer;
      if (!document.getElementById("spotify-web-playback-sdk")) {
        const script = document.createElement("script");
        script.id = "spotify-web-playback-sdk";
        script.src = "https://sdk.scdn.co/spotify-player.js";
        script.async = true;
        script.onerror = () => {
          console.warn("Failed to load Web Playback SDK - falling back to Spotify app");
          sdkFailedRef.current = true;
        };
        document.body.appendChild(script);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Disconnect SDK player on unmount
  useEffect(() => {
    return () => {
      if (sdkPlayerRef.current) {
        sdkPlayerRef.current.disconnect();
        sdkPlayerRef.current = null;
        sdkDeviceIdRef.current = null;
      }
    };
  }, []);

  // Reset player UI state when becoming visible again
  useEffect(() => {
    if (!hideSpotifyPlayerUI) {
      // Clear any leftover messages and reset timer state
      setMessage("");
      setPlayTimer(0);
      setIsPlaying(false);

      // Clear any running timers
      clearInterval(timerInterval.current);
      clearInterval(trackCheckInterval.current);
      timerInterval.current = null;
      trackCheckInterval.current = null;
    }
  }, [hideSpotifyPlayerUI, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerInterval.current);
      clearInterval(trackCheckInterval.current);
    };
  }, []);

// Reset + preload metadata when a new songToPlay comes in
useEffect(() => {
  if (!songToPlay || songToPlay === "0") {
    return;
  }

  // Stop timers
  clearInterval(timerInterval.current);
  clearInterval(trackCheckInterval.current);
  timerInterval.current = null;
  trackCheckInterval.current = null;

  // Reset all playback state
  setPosition(0);
  setDuration(0);
  setTrack({});
  // Don't set "Loading and starting playback..." here - only set it when playSong() is actually called
  // This prevents getting stuck on that message after page reload
  setMessage("");
  setIsPlaying(false);
  setPlayTimer(0);

  // Clear restart button when new song is selected
  dispatch({
    type: "SET_SHOWRESTARTBUTTON",
    showRestartButton: false,
  });

  // 🔴 Pause current playback if something is running
  spotifyRateLimiter.makeRequest(`https://api.spotify.com/v1/me/player/pause`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => {
    console.warn("Pause failed — maybe nothing was playing");
  });

  // 🎨 Fetch metadata for new track immediately
  console.log("🎵 SpotifyPlayer: Fetching metadata for track ID:", songToPlay);
  spotifyRateLimiter.makeRequest(`https://api.spotify.com/v1/tracks/${songToPlay}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => res.json())
    .then((data) => {
      if (data && data.id) {
        console.log("🎵 SpotifyPlayer: Metadata fetched for:", data.name, "by", data.artists?.[0]?.name, "ID:", data.id);
        setTrack(data); // update track info immediately
      }
    })
    .catch((err) => console.error("Error fetching track metadata:", err));
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [songToPlay]);

// 🎵 Direct play trigger from user click
useEffect(() => {
  if (triggerPlayFromClick && songToPlay !== "0") {
    // Call playSong directly without setTimeout to maintain user gesture
    playSong();

    // Reset trigger
    dispatch({
      type: "SET_TRIGGERPLAYFROMCLICK",
      triggerPlayFromClick: false,
    });
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [triggerPlayFromClick, songToPlay]);

  // Check Spotify App Status (all devices now use Spotify app)
  // Returns the device to play on, or null if no Spotify app is available.
  // Uses /me/player/devices instead of /me/player so an open-but-idle desktop app
  // is found too, and prefers the device type matching the platform (desktop app on
  // desktop) so playback doesn't end up on e.g. the phone.
  const checkIOSSpotifyStatus = async () => {
    if (!token) return null;

    try {
      const response = await spotifyRateLimiter.makeRequest(
        "https://api.spotify.com/v1/me/player/devices",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      const devices = (data?.devices || []).filter((d) => d.id && !d.is_restricted);
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      const preferredType = isMobile ? "Smartphone" : "Computer";
      const device =
        devices.find((d) => d.type === preferredType && d.is_active) ||
        devices.find((d) => d.type === preferredType) ||
        devices.find((d) => d.is_active) ||
        devices[0];

      if (device) {
        // Spotify app is open
        setIsSpotifyAppOpen(true);
        setShowIOSSetup(false);
        return device;
      }

      // No device found - Spotify app is probably closed
      setIsSpotifyAppOpen(false);
      setShowIOSSetup(true);
      return null;
    } catch (error) {
      console.log("iOS Spotify status check error:", error);
      // Assume Spotify app is closed on error
      setIsSpotifyAppOpen(false);
      setShowIOSSetup(true);
      return null;
    }
  };




const playSong = async () => {
  console.log("🎵 SpotifyPlayer playSong() called with songToPlay:", songToPlay);

  // Reset progress and timer when play is clicked
  setPosition(0);
  setPlayTimer(0);
  setMessage("Loading and starting playback...");
  setIsPlaying(false);

  try {
    if (isSdkPlayback()) {
      // Desktop: play in the browser via Web Playback SDK.
      // activateElement() must run synchronously within the click to satisfy autoplay rules.
      sdkPlayerRef.current.activateElement();
      console.log('💻 Playing via Web Playback SDK on device:', sdkDeviceIdRef.current);

      // makeRequest rejects on non-2xx responses, so reaching the next line means success
      await spotifyRateLimiter.makeRequest(
        `https://api.spotify.com/v1/me/player/play?device_id=${sdkDeviceIdRef.current}`,
        {
          method: "PUT",
          body: JSON.stringify({
            uris: [`spotify:track:${songToPlay}`],
            position_ms: 0,
          }),
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      playStartedAt.current = Date.now();
      setIsPlaying(true);
      setMessage("");
      console.log('✅ Started playback via Web Playback SDK');

      dispatch({
        type: "SET_SHOWINITIALIZEPLAYERBUTTON",
        showInitializePlayerButton: 1,
      });
      return;
    }

    // Mobile (or SDK unavailable): check if Spotify app is ready
    const device = await checkIOSSpotifyStatus();

    if (!device) {
      // Show setup screen
      setShowIOSSetup(true);
      return;
    }

    // All devices: Play on the selected Spotify app device (device_id also activates an idle app)
    console.log('📱 Playing on Spotify app device:', device.name, device.type, device.is_active ? '(active)' : '(inactive)');

    if (device.is_active) {
      // Pause current playback first
      await spotifyRateLimiter.makeRequest(`https://api.spotify.com/v1/me/player/pause`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {}); // ignore if nothing playing
    } else {
      // Idle desktop app (or another device is active): transfer playback to it first,
      // otherwise Spotify often accepts the play command but the app doesn't start
      await spotifyRateLimiter.makeRequest(`https://api.spotify.com/v1/me/player`, {
        method: "PUT",
        body: JSON.stringify({ device_ids: [device.id], play: false }),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }).catch((e) => console.warn("Transfer playback failed:", e));
      await new Promise((resolve) => setTimeout(resolve, 700));
    }

    // Play on selected Spotify device
    await spotifyRateLimiter.makeRequest(
      `https://api.spotify.com/v1/me/player/play?device_id=${device.id}`,
      {
        method: "PUT",
        body: JSON.stringify({
          uris: [`spotify:track:${songToPlay}`],
          position_ms: 0,
        }),
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    // Manually trigger playing state since we don't have player state listener
    playStartedAt.current = Date.now();
    setIsPlaying(true);
    setMessage(""); // Clear any previous error messages

    console.log('✅ Started playback on Spotify app');
  } catch (err) {
    console.error("Error starting playback:", err);
    if (isSdkPlayback()) {
      setMessage("Error starting playback. Please try again.");
      dispatch({
        type: "SET_SHOWINITIALIZEPLAYERBUTTON",
        showInitializePlayerButton: 1,
      });
      return;
    }
    // Check Spotify status again on error
    const spotifyReady = await checkIOSSpotifyStatus();
    if (!spotifyReady) {
      setShowIOSSetup(true);
    } else {
      setMessage(`Could not control Spotify app. Please open Spotify app and start playing any song, then click Resume Playback below.`);
      // Show restart button for errors
      dispatch({
        type: "SET_SHOWRESTARTBUTTON",
        showRestartButton: true,
      });
    }
  }

  dispatch({
    type: "SET_SHOWINITIALIZEPLAYERBUTTON",
    showInitializePlayerButton: 1,
  });
};




  // const onSeek = (e) => {
  //   const newPosSeconds = Number(e.target.value);
  //   if (!deviceId) return;

  //   fetch(
  //     `https://api.spotify.com/v1/me/player/seek?position_ms=${newPosSeconds * 1000}&device_id=${deviceId}`,
  //     {
  //       method: "PUT",
  //       headers: { Authorization: `Bearer ${token}` },
  //     }
  //   );

  //   setPosition(newPosSeconds);
  // };

  // updateProgress is now defined above

  // 🧠 Manage play timer reactively based on isPlaying
  useEffect(() => {
    if (isPlaying) {
      // Clear restart button when playback starts normally
      dispatch({
        type: "SET_SHOWRESTARTBUTTON",
        showRestartButton: false,
      });

      // Start track checking when song is playing
      startTrackChecking();

      if (!timerInterval.current) {
        timerInterval.current = setInterval(() => {
          setPlayTimer((prev) => {
            const next = prev + 1;
            // Sync playTimer with position so the timer increments smoothly
            // Use calculated listening time (accounts for short songs)
            if (next === listeningTimeRequired + 1) {
              setMessage("Great, now you can rate this song in its box below! Then click 'Play Song' for the next song.");
              clearInterval(timerInterval.current);
              timerInterval.current = null;
              // Stop track checking when timer ends
              stopTrackChecking();

          dispatch({
      type: "SET_USERDARFRATEN",
     userDarfRaten: 1,
    });

          // Mark this song as listened to for 33+ seconds
          dispatch({
      type: "SET_SONGLISTENEDTO",
     songId: songToPlay,
     listened: true,
    });

//NEU DISCONNECT PLAYER
    //       dispatch({
    //   type: "SET_DISCONNECTPLAYER",
    //  disconnectPlayer: true,
    // });




          
    //NEU PLAYER VERSTECKEN NACH 37 SEK.
    //           dispatch({
    //   type: "SET_SHOWSPOTIFYPLAYER",
    //  showSpotifyPlayer: 0,
    // });


  }







            return next;
          });
        }, 1000);
      }
    } else {
      // Stop track checking when song is paused
      stopTrackChecking();

      // Use calculated listening time (accounts for short songs)
      if (playTimer > 0 && playTimer < listeningTimeRequired) {
        // Don't overwrite the wrong-song error message with pause message
        if (message !== "Error. The song you should rate is not being played back!" &&
            message !== "Wrong song playing! Click Resume Playback below to play the correct song.") {
          // Show fullscreen error modal for all devices
          setShowIOSErrorModal('pause');
        }
        // Show restart button when user pauses
        dispatch({
          type: "SET_SHOWRESTARTBUTTON",
          showRestartButton: true,
        });
      }
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    }

    return () => {
      clearInterval(timerInterval.current);
      timerInterval.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  // Desktop SDK: update progress bar from local player state every second
  useEffect(() => {
    if (!isPlaying || !isSdkPlayback()) return;

    const updateProgress = async () => {
      const state = await sdkPlayerRef.current?.getCurrentState();
      if (!state) return;
      setPosition(Math.floor(state.position / 1000));
      setDuration(Math.floor(state.duration / 1000));
    };

    updateProgress();
    progressInterval.current = setInterval(updateProgress, 1000);

    return () => {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  // Format time as mm:ss
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  // Function to check currently playing track via Spotify Web API with rate limiting
  const checkCurrentlyPlayingTrack = async () => {
    if (!token || !songToPlay || songToPlay === "0") return;

    // Desktop SDK: read local player state instead of calling the Web API
    if (isSdkPlayback()) {
      checkSdkPlayerState();
      return;
    }

    try {
      const response = await spotifyRateLimiter.makeRequest(
        "https://api.spotify.com/v1/me/player/currently-playing",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        const data = await response.json();

        if (data && data.item) {
          const currentlyPlayingId = data.item.id;
          const expectedId = songToPlay;

          // Spotify needs a moment to report the new track/state, so don't flag
          // a pause or the previous song as "wrong" right after starting playback
          const justStarted = Date.now() - playStartedAt.current < 5000;

          // Check if playback is paused (important for mobile detection)
          if (!data.is_playing && isPlaying && !justStarted) {
            // User paused - update local state to trigger pause message
            setIsPlaying(false);
          } else if (data.is_playing && !isPlaying) {
            // User resumed - update local state
            setIsPlaying(true);
          }

          if (data.is_playing && currentlyPlayingId !== expectedId && !justStarted) {
            // Show fullscreen error modal for all devices
            setShowIOSErrorModal('wrongSong');

            // Show restart button when wrong song is detected
            dispatch({
              type: "SET_SHOWRESTARTBUTTON",
              showRestartButton: true,
            });

            // Pause the wrong song
            try {
              await spotifyRateLimiter.makeRequest("https://api.spotify.com/v1/me/player/pause", {
                method: "PUT",
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              });
            } catch (pauseError) {
              console.error("Failed to pause wrong song:", pauseError);
            }
          } else if (data.is_playing && currentlyPlayingId === expectedId) {
            // Clear the error message if the correct song is playing
            if (message === "Error. The song you should rate is not being played back!") {
              setMessage("");
              // Hide restart button when correct song is playing
              dispatch({
                type: "SET_SHOWRESTARTBUTTON",
                showRestartButton: false,
              });
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking currently playing track:", error);
    }
  };

  // Pause / wrong-song detection for the Web Playback SDK player
  const checkSdkPlayerState = async () => {
    const player = sdkPlayerRef.current;
    if (!player) return;

    const justStarted = Date.now() - playStartedAt.current < 5000;
    if (justStarted) return;

    const state = await player.getCurrentState();

    // null = playback was moved to another device
    if (!state) {
      if (isPlaying) setIsPlaying(false);
      return;
    }

    const currentTrack = state.track_window?.current_track;
    const isExpectedTrack =
      currentTrack?.id === songToPlay || currentTrack?.linked_from?.id === songToPlay;

    if (state.paused && isPlaying) {
      setIsPlaying(false);
    } else if (!state.paused && !isPlaying) {
      setIsPlaying(true);
    }

    if (!state.paused && !isExpectedTrack) {
      setShowIOSErrorModal('wrongSong');
      dispatch({
        type: "SET_SHOWRESTARTBUTTON",
        showRestartButton: true,
      });
      player.pause().catch((e) => console.error("Failed to pause wrong song:", e));
    }
  };

  // Start track checking when a song is set to play
  const startTrackChecking = () => {
    if (trackCheckInterval.current) {
      clearInterval(trackCheckInterval.current);
    }

    // Check frequently for better pause/wrong-song detection (2s interval)
    // All users control playback via the Spotify app, so we need responsive detection
    trackCheckInterval.current = setInterval(checkCurrentlyPlayingTrack, 2000);
  };

  // Stop track checking
  const stopTrackChecking = () => {
    if (trackCheckInterval.current) {
      clearInterval(trackCheckInterval.current);
      trackCheckInterval.current = null;
    }
  };

  // Page Visibility API: Check track immediately when user returns to browser
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isPlaying) {
        // User just returned to the browser - check track status immediately
        console.log("📱 User returned to browser - checking track status");
        checkCurrentlyPlayingTrack();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);


  // Eject song: reset player to initial state with no song loaded
  const handleEjectSong = async () => {
    // Pause playback on Spotify
    try {
      await spotifyRateLimiter.makeRequest(`https://api.spotify.com/v1/me/player/pause`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (e) {
      // ignore – maybe nothing was playing
    }

    // Stop all intervals
    clearInterval(timerInterval.current);
    clearInterval(trackCheckInterval.current);
    timerInterval.current = null;
    trackCheckInterval.current = null;

    // Reset local player state
    setPosition(0);
    setDuration(0);
    setTrack({});
    setMessage("");
    setIsPlaying(false);
    setPlayTimer(0);
    setShowIOSSetup(false);
    setShowIOSErrorModal(null);

    // Reset global state so another song can be played
    dispatch({ type: "SET_SONGTOPLAY", songToPlay: "0" });
    dispatch({ type: "SET_PLAYBLOCKER", playBlocker: 0 });
    dispatch({ type: "SET_SHOWRESTARTBUTTON", showRestartButton: false });
    dispatch({ type: "SET_SHOWINITIALIZEPLAYERBUTTON", showInitializePlayerButton: 0 });
    dispatch({ type: "SET_USERDARFRATEN", userDarfRaten: 0 });

    // Clear localStorage entry for currently playing song
    localStorage.removeItem('currentlyPlayingSong');
  };

  // Handler for resuming playback from error modal
  const handleErrorModalResume = async () => {
    setShowIOSErrorModal(null);
    setMessage("");
    // Replay the song
    if (songToPlay && songToPlay !== "0") {
      await playSong();
    }
  };

  return (
    <>
      {/* Spotify Setup Screen */}
      {showIOSSetup && (
        <IOSSpotifySetup
          isSpotifyAppOpen={isSpotifyAppOpen}
          onContinue={async () => {
            setShowIOSSetup(false);
            // Try to play the song after user confirms
            if (songToPlay && songToPlay !== "0") {
              await playSong();
            }
          }}
        />
      )}

      {/* Error Modal */}
      {showIOSErrorModal && (
        <IOSErrorModal
          errorType={showIOSErrorModal}
          onResume={handleErrorModalResume}
        />
      )}

      <div className="spotify-player" style={{
        display: hideSpotifyPlayerUI ? "none" : "block",
      }}>

      {/* Single Row Layout */}
      <div className="player-content-row">
        {/* Track Info */}
        {track?.name ? (
          <div className="track-info">
            <img
              src={track.album?.images?.[0]?.url}
              alt="Album cover"
              className="track-info-album-art"
            />
            <div className="track-info-details">
              <div className="track-info-name">{track.name}</div>
              <div className="track-info-artist">
                {track.artists?.map((a) => a.name).join(", ")}
              </div>
            </div>
          </div>
        ) : (
          <div className="track-info">
            <div className="track-info-details">
              <div className="track-info-name">Loading...</div>
            </div>
          </div>
        )}

        {/* Timer and Status Container */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          {/* Timer */}
          {playTimer > 0 && (
            <div className="play-timer" style={{ minWidth: '120px' }}>
              {formatTime(playTimer)} / {formatTime(listeningTimeRequired)}
            </div>
          )}

          {/* Message or Status */}
          <div style={{ textAlign: 'center', padding: '0 10px' }}>
            {message ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                <span style={{
                  color: message.includes("Error") || message.includes("Wrong") ? '#dc2626' : '#059669',
                  fontSize: '14px'
                }}>
                  {message}
                </span>
              </div>
            ) : playTimer > 0 ? (
              <span style={{ color: '#059669', fontSize: '14px' }}>Playing...</span>
            ) : (
              <span style={{ color: '#6b7280', fontSize: '14px' }}>Ready</span>
            )}
          </div>

          {/* Progress Bar - Desktop (Web Playback SDK) only */}
          {!isMobile && duration > 0 && (
            <div className="progress-container" style={{ width: '100%', maxWidth: '400px' }}>
              <div className="progress-bar">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${Math.min(100, (position / duration) * 100)}%` }}
                />
              </div>
              <div className="progress-time">
                <span>{formatTime(position)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Reset Button - Only show on playback error */}
        {message && message.includes("Error starting playback. Please try again.") && (
          <div style={{ display: 'flex', alignItems: 'center', paddingRight: '10px' }}>
            <button
              className="reset-button"
              onClick={() => window.location.reload()}
              title="Reset player (same as browser refresh)"
            >
              Reset
            </button>
          </div>
        )}

        {/* Eject Song Button - Only show when a song is loaded */}
        {songToPlay && songToPlay !== "0" && (
          <div style={{ display: 'flex', alignItems: 'center', paddingRight: '4px' }}>
            <button
              className="eject-button"
              onClick={handleEjectSong}
              title="Eject song and reset player"
            >
              Eject song
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
}
